import { defineStore } from "pinia";
import { usePwaStore } from "@/stores/pwa";
import { useAuthStore } from "@/stores/auth";
import axios from "@/plugins/axios";
import type { WoodlandSurvey } from "@/types/survey";
import SurveyService from "@/services/SurveyService";
import { useAlertStore } from "@/stores/alert";

/**
 * A store module for managing the woodland survey resource.
 *
 * In the following, when interacting with the API, errors are handled by:
 * 1. setting requireSync to true to recover via syncing
 * 2. rethrow the error for console output and for BugSnag
 */
export const useWoodlandSurveyStore = defineStore("woodlandSurvey", () => {
	/**
	 * Store a client by calling the API client store endpoint.
	 */
	const store = async function (woodlandSurvey: WoodlandSurvey): Promise<void> {
		const authStore = useAuthStore();
		const pwaStore = usePwaStore();

		const now = new Date();
		const isoString = now.toISOString();//.replace("T", " ");
		woodlandSurvey.created_at = isoString;
		// woodlandSurvey.synced_at = isoString;

		// Try to persist it
		if (pwaStore.onlineAndConnected && pwaStore.manuallyOnline) {
			await axios.post(route("api.woodland_survey.store"), woodlandSurvey).then((response)=>{
				// console.log(response);
				const woodlandSurveyReturned = response.data.data;
				// console.log("added new woodland survey");
				// gets the synced_at version to match the DB, and adopts the
				// server's created_at/updated_at (it may overwrite them on save)
				woodlandSurvey.synced_at = woodlandSurveyReturned.synced_at;
				woodlandSurvey.created_at = woodlandSurveyReturned.created_at;
				woodlandSurvey.updated_at = woodlandSurveyReturned.updated_at;
			}).catch(error => {
				console.error("create woodland failed: " + error);

				pwaStore.requireSync = true;
				throw error;
			});
		}
		else {
			pwaStore.requireSync = true;
		}

		// Update the current user store
		for (const client of authStore.user.clients) {
			for (const estate of client.estates) {
				const site = estate.sites.find((x: any) => x.id == woodlandSurvey.site_id);
				if (site) {
					site.woodland_surveys.push(woodlandSurvey);
					break;
				}
			}
		}

	};

	/**
	 * Update a WoodlandSurvey by calling the API WoodlandSurvey update endpoint.
	 */
	const update = async function (woodlandSurvey: WoodlandSurvey): Promise<{ survey: WoodlandSurvey; goBack: boolean }> {
		const pwaStore = usePwaStore();
		const alertStore = useAlertStore();
		const surveyService = new SurveyService();
		const now = new Date();
		const isoString = now.toISOString();//.replace("T", " ");
		woodlandSurvey.updated_at = isoString;
		if (pwaStore.onlineAndConnected && pwaStore.manuallyOnline) {
			// send the object to the api
			return await surveyService.updateWoodlandSurvey(woodlandSurvey)
				.then((response) => {
					const woodlandSurveyReturned = response.data.data;
					if (response.data.code == 422) {
						console.warn("(1) Data in the database is newer than this Woodland Survey. Could not overwrite. Data has now been synced for this woodland survey but we recommend manually syncing your account. (Click on your name, then 'manually sync'.");
						alertStore.info("Survey data in the database is newer than this Woodland  Survey. Data has now been synced for this woodland survey. Please check that your recent change has been saved. We recommend manually syncing your account. (Click on your name, then 'manually sync)'.");
						alert("Survey data in the database is newer than this Woodland  Survey. Data has now been synced for this woodland survey. Please check that your recent change has been saved. We recommend manually syncing your account. (Click on your name, then 'manually sync)'.");
						return { survey: woodlandSurveyReturned, goBack: true };
					}
					// assuming there hasnt been a real error then we want to reset to
					// the version returned by the attempted sync:
					woodlandSurvey.synced_at = woodlandSurveyReturned.synced_at;
					woodlandSurvey.updated_at = woodlandSurveyReturned.updated_at;
					return { survey: woodlandSurvey, goBack: false };
				}).catch((error) => {
					console.error(error);
					return { survey: woodlandSurvey, goBack: false };
				});

		}
		else {
			// not online in order to sync so ensure sync is flagged for when we come online
			pwaStore.requireSync = true;
			return { survey: woodlandSurvey, goBack: false };
		}

	};

	/**
	 * Fetch the authoritative copy of a WoodlandSurvey from the server and, if it
	 * has been completed since this device last synced, adopt it locally. This
	 * stops a device that cached the survey before it was submitted elsewhere
	 * from continuing to show it as incomplete/provisional after opening it.
	 *
	 * We only adopt the server copy when it is complete: if it's still
	 * incomplete we leave the local copy alone so any offline edits in
	 * progress on this device aren't clobbered.
	 */
	const refresh = async function (surveyId: string): Promise<void> {
		const authStore = useAuthStore();
		const pwaStore = usePwaStore();

		if (!pwaStore.onlineAndConnected || !pwaStore.manuallyOnline) return;

		try {
			const response = await axios.get(route("api.woodland_survey.show", { woodland_survey: surveyId }));
			const woodlandSurveyReturned = response.data.data;

			if (!woodlandSurveyReturned || Number(woodlandSurveyReturned.complete) !== 1) return;

			for (const client of authStore.user.clients) {
				for (const estate of client.estates) {
					const site = estate.sites.find((x: any) => x.id == woodlandSurveyReturned.site_id);
					if (site) {
						const index = site.woodland_surveys.findIndex((x: any) => x.id == surveyId);
						if (index !== -1) {
							site.woodland_surveys[index] = woodlandSurveyReturned;
						}
						return;
					}
				}
			}
		} catch (error) {
			console.error(error);
		}
	};

	/**
	 * Destroy a WoodlandSurvey by calling the API WoodlandSurvey destroy endpoint.
	 */
	const destroy = async function (woodlandSurvey: WoodlandSurvey): Promise<void> {
		const authStore = useAuthStore();
		const pwaStore = usePwaStore();

		// Mark the WoodlandSurvey as deleted
		const now = new Date();
		const isoString = now.toISOString();//.replace("T", " ");
		woodlandSurvey.deleted_at = isoString;
		woodlandSurvey.updated_at = isoString;

		// Try to persist it
		if (pwaStore.onlineAndConnected && pwaStore.manuallyOnline) {
			await axios.delete(route("api.woodland_survey.destroy", { id: woodlandSurvey.id })).then(() => {
				// console.log("deleted WoodlandSurvey");
				// Remove local soft-deleted WoodlandSurveys by refetching data
				authStore.getCurrentUser();
			}).catch(error=>{
				pwaStore.requireSync = true;
				console.error(error.code);
				throw error;
			});
		}
		else {
			pwaStore.requireSync = true;
		}
	};

	return { store, update, destroy, refresh };
}, {
	persist: {
		// Ensure none of this store is persisted. Clients are persisted via the
		// current user store.
		paths: []
	}
});

export const woodlandSurveyDefaults: Pick<WoodlandSurvey, "q01" | "q02" | "q03" | "q04" | "q05" | "q06" | "q07" | "q08" | "q09" | "q10" | "q11" | "q12" | "q13" | "q14" | "q15" | "resultWCA" | "resultBNG" | "n_plots_b" | "n_plots_c" | "n_plots_m" | "permit_full_share" | "permit_partial_share" | "xls_string"> = {

	q01: { b: 0, c: 0, m: 0 },
	q02: { b: 0, c: 0, m: 0 },
	q03: { b: 0, c: 0, m: 0 },
	q04: { b: 0, c: 0, m: 0 },
	q05: { b: 0, c: 0, m: 0 },
	q06: { b: 0, c: 0, m: 0 },
	q07: { b: 0, c: 0, m: 0 },
	q08: { b: 0, c: 0, m: 0 },
	q09: { b: 0, c: 0, m: 0 },
	q10: { b: 0, c: 0, m: 0 },
	q11: { b: 0, c: 0, m: 0 },
	q12: { b: 0, c: 0, m: 0 },
	q13: { b: 0, c: 0, m: 0 },
	q14: { b: 0, c: 0, m: 0 },
	q15: { b: 0, c: 0, m: 0 },
	resultWCA: { b: 0, c: 0, m: 0, advice_b: [], advice_c: [], advice_m: [], numQs: 15 },
	resultBNG: { b: 0, c: 0, m: 0, advice_b: [], advice_c: [], advice_m: [], numQs: 13 },
	n_plots_b: 0,
	n_plots_c: 0,
	n_plots_m: 0,
	permit_full_share: 1,
	permit_partial_share: 0,
	xls_string: ""
};
